import React, { useState, useRef, useEffect } from 'react';
import { postToNetSuite, reportSessionTiming } from '../api/authService';
import { useOrderPolling } from '../hooks/useOrderPolling';

// Extract sessionId from URL
const useSessionId = (): string | null => {
  const [sessionId, setSessionId] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const session = params.get('session');
    if (session) setSessionId(session);
  }, []);

  return sessionId;
};

const TAB_SESSION_KEY = 'activePunchoutSession';

const styles: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "'Segoe UI', Arial, sans-serif",
    maxWidth: 600,
    margin: '0 auto',
    padding: '2rem 2rem',
    color: '#1a1a2e',
  },
  heading: {
    fontSize: '1.75rem',
    fontWeight: 700,
    marginBottom: '1.5rem',
    color: '#1a1a2e',
  },
  card: {
    background: '#f8f9fa',
    border: '1px solid #e0e0e0',
    borderRadius: 12,
    padding: '1.5rem',
    marginTop: '1.5rem',
  },
  cardHeading: {
    fontSize: '1.15rem',
    fontWeight: 600,
    marginBottom: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  subText: {
    color: '#555',
    fontSize: '0.95rem',
    marginBottom: '1rem',
  },
  expiredCard: {
    background: '#fff5f5',
    border: '1px solid #f5c6c6',
    borderRadius: 12,
    padding: '1.5rem',
    marginTop: '1.5rem',
  },
  expiredHeading: {
    color: '#c0392b',
    fontSize: '1.15rem',
    fontWeight: 600,
    marginBottom: '0.5rem',
  },
};

/* Injected styles: spinner animation + button hover/active states */
const InjectStyles = () => (
  <style>{`
    @keyframes punchout-spin {
      to { transform: rotate(360deg); }
    }
    .punchout-spinner {
      display: inline-block;
      width: 22px;
      height: 22px;
      border: 3px solid #d0d0d0;
      border-top-color: #1a73e8;
      border-radius: 50%;
      animation: punchout-spin 0.8s linear infinite;
      flex-shrink: 0;
    }
    .punchout-btn {
      background-color: #1a73e8;
      color: #fff;
      border: none;
      border-radius: 8px;
      padding: 0.6rem 1.4rem;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      letter-spacing: 0.3px;
      transition: background-color 0.15s ease, transform 0.1s ease, box-shadow 0.1s ease;
      box-shadow: 0 2px 4px rgba(0,0,0,0.15);
    }
    .punchout-btn:hover {
      background-color: #1558b0;
      box-shadow: 0 4px 8px rgba(0,0,0,0.2);
    }
    .punchout-btn:active {
      background-color: #0d3f80;
      transform: translateY(1px);
      box-shadow: 0 1px 2px rgba(0,0,0,0.15);
    }
  `}</style>
);

const MainPage: React.FC = () => {
  const sessionId = useSessionId();
  const popupRef = useRef<Window | null>(null);

  const popupMonitorRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isStartingRef = useRef(false);

  // Session-length timing: start is stamped when the pop-up opens, end when it closes.
  const sessionStartRef = useRef<number | null>(null);
  const timingReportedRef = useRef(false);

  const [sessionInProgress, setSessionInProgress] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [pollingEnabled, setPollingEnabled] = useState(true);

  const {
    orderInfo,
    isOrderReady,
    returnURL,
    isExpired
  } = useOrderPolling(sessionId, pollingEnabled);

  /* -------------------- Session Timing Report -------------------- */
  // Idempotent: the pop-up can close via several paths (manual close, order-ready/expiry
  // cleanup, tab unload) and each may call this — only the first send wins.
  const reportTiming = () => {
    if (timingReportedRef.current) return;
    if (!sessionId || sessionStartRef.current == null) return;
    timingReportedRef.current = true;
    reportSessionTiming(
      sessionId,
      new Date(sessionStartRef.current).toISOString(),
      new Date().toISOString()
    );
  };

  /* -------------------- Popup Close Monitor -------------------- */
  const startPopupMonitor = (popup: Window) => {
    if (popupMonitorRef.current) clearInterval(popupMonitorRef.current);
    popupMonitorRef.current = setInterval(() => {
      if (popup.closed) {
        clearInterval(popupMonitorRef.current!);
        popupMonitorRef.current = null;
        reportTiming(); // pop-up closed — record session end
        // No state reset here — polling and cleanup useEffect own that logic
      }
    }, 1000);
  };

  /* -------------------- Tab/iframe Close Cleanup -------------------- */
  useEffect(() => {
    const handlePageHide = () => {
      if (!sessionId || (!sessionInProgress && !isPreparing)) return;
      fetch(`/api/netsuite/session/${encodeURIComponent(sessionId)}`, {
        method: 'DELETE',
        keepalive: true,
      }).catch(() => {}); // best-effort, swallow errors

      // Tab is unloading mid-session — record session end too. axios won't survive unload, so
      // use keepalive fetch. Guarded so we never double-report if the pop-up already closed.
      if (!timingReportedRef.current && sessionStartRef.current != null) {
        timingReportedRef.current = true;
        fetch('/api/netsuite/session-timing', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId,
            startTime: new Date(sessionStartRef.current).toISOString(),
            endTime: new Date().toISOString(),
          }),
          keepalive: true,
        }).catch(() => {});
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [sessionId, sessionInProgress, isPreparing]);

  /* -------------------- Start PunchOut -------------------- */
  const handlePunchout = async () => {
    if (isStartingRef.current) return;
    isStartingRef.current = true;
    setIsPreparing(true);

    // Fresh session — clear any prior timing state.
    sessionStartRef.current = null;
    timingReportedRef.current = false;

    if (!sessionId) {
      isStartingRef.current = false;
      setIsPreparing(false);
      alert('Session ID not found.');
      return;
    }

    // Prevent multiple PunchOuts in the same tab
    const activeSession = sessionStorage.getItem(TAB_SESSION_KEY);
    if (activeSession && activeSession !== sessionId) {
      isStartingRef.current = false;
      setIsPreparing(false);
      alert('A PunchOut session is already active in this tab.');
      return;
    }

    try {
      setPollingEnabled(true);
      sessionStorage.setItem(TAB_SESSION_KEY, sessionId);

      const startUrl = await postToNetSuite(sessionId);

      const popup = window.open(
        startUrl,
        `PunchoutWindow-${sessionId}`,
        'width=1200,height=800'
      );

      if (!popup) {
        isStartingRef.current = false;
        setIsPreparing(false);
        alert('Please allow pop-ups to continue.');
        sessionStorage.removeItem(TAB_SESSION_KEY);
        return;
      }

      popupRef.current = popup;
      sessionStartRef.current = Date.now(); // session start = pop-up opened
      startPopupMonitor(popup);
      popup.focus();
      setIsPreparing(false);
      setSessionInProgress(true);
    } catch (err) {
      isStartingRef.current = false;
      setIsPreparing(false);
      console.error('Failed to start PunchOut:', err);
      sessionStorage.removeItem(TAB_SESSION_KEY);
    }
  };

  /* -------------------- Session End Cleanup -------------------- */
  useEffect(() => {
    if (isOrderReady || isExpired) {
      // Record session end before tearing down the monitor — clearing its interval would
      // otherwise pre-empt the monitor's own pop-up-close detection (idempotent either way).
      reportTiming();

      setSessionInProgress(false);
      setIsPreparing(false);
      isStartingRef.current = false;
      sessionStorage.removeItem(TAB_SESSION_KEY);

      if (popupMonitorRef.current) {
        clearInterval(popupMonitorRef.current);
        popupMonitorRef.current = null;
      }

      // Close popup if still open
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    }
    // Intentionally keyed only on the end signals; reportTiming reads refs and is idempotent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrderReady, isExpired]);

  /* -------------------- Expiration Notification -------------------- */
  useEffect(() => {
    if (isExpired) {
      alert('Your PunchOut session has expired. Please start again.');
    }
  }, [isExpired]);

  /* -------------------- Return PunchOut Order -------------------- */
  const handleSendOrder = () => {
    if (!orderInfo || !returnURL || !sessionId) return;

    console.log('Returning PunchOut session:', sessionId);

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = returnURL;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'cxml-urlencoded';
    input.value = orderInfo;

    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
  };

  const isIdle = !isPreparing && !sessionInProgress && !isOrderReady && !isExpired;

  /* -------------------- UI -------------------- */
  return (
    <div style={styles.page}>
      <InjectStyles />
      <h1 style={styles.heading}>PunchOut</h1>

      {/* ── Idle: prompt + button ── */}
      {isIdle && (
        <div style={styles.card}>
          <div style={styles.cardHeading}>Ready to Shop</div>
          <p style={styles.subText}>
            Please click the button below to start your PunchOut session and begin shopping.
          </p>
          <button className="punchout-btn" onClick={handlePunchout}>
            Start PunchOut
          </button>
        </div>
      )}

      {/* ── Preparing: spinner while session is being set up ── */}
      {isPreparing && (
        <div style={styles.card}>
          <div style={styles.cardHeading}>
            <span className="punchout-spinner" />
            Preparing Your Session
          </div>
          <p style={styles.subText}>Please wait while we connect to the supplier catalog…</p>
        </div>
      )}

      {/* ── Session open in popup ── */}
      {sessionInProgress && (
        <div style={styles.card}>
          <div style={styles.cardHeading}>
            <span className="punchout-spinner" />
            Session In Progress
          </div>
          <p style={styles.subText}>
            Please complete your shopping in the supplier window and check out to continue.
          </p>
        </div>
      )}

      {/* ── Order returned from supplier ── */}
      {isOrderReady && (
        <div style={styles.card}>
          <div style={styles.cardHeading}>
            <span style={{ fontSize: '1.3rem', color: '#27ae60' }}>✔</span>
            Order Ready
          </div>
          <p style={styles.subText}>
            Your cart has been received. Click below to return it to the buying application.
          </p>
          <button className="punchout-btn" onClick={handleSendOrder}>
            Return to Application
          </button>
        </div>
      )}

      {/* ── Session expired ── */}
      {isExpired && (
        <div style={styles.expiredCard}>
          <div style={styles.expiredHeading}>Session Expired</div>
          <p style={{ ...styles.subText, marginBottom: '1rem' }}>
            Your PunchOut session expired due to inactivity.
          </p>
          <button className="punchout-btn" onClick={() => window.location.reload()}>
            Start New PunchOut
          </button>
        </div>
      )}
    </div>
  );
};

export default MainPage;
